import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import "./cookie-consent.component.styles.scss";
import {
  readConsent,
  writeConsent,
  rehydrateConsent,
  LVS_CONSENT_EVENT,
} from "../../../lib/consent";
import { useLang } from "../../../lib/i18n";

/**
 * Cookie consent banner.
 *
 * GDPR / Italian Garante compliance:
 *   - No non-technical cookies fire before the user makes a decision
 *     (GTM is gated behind this banner; Consent Mode v2 defaults to
 *     "denied" in index.html).
 *   - The user can Accept, Reject, or open a preferences drawer.
 *   - The decision is stored in localStorage and re-applied on future
 *     visits. A policy version bump re-prompts everyone.
 */
const CookieConsent: React.FC = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [analytics, setAnalytics] = useState(false);
  const [marketing, setMarketing] = useState(false);
  const { lang, lp } = useLang();
  const isEn = lang === "en";

  useEffect(() => {
    // On mount, re-apply stored consent (in case user accepted previously).
    rehydrateConsent();
    const existing = readConsent();
    if (!existing) setVisible(true);
  }, []);

  useEffect(() => {
    const handler = () => setVisible(false);
    window.addEventListener(LVS_CONSENT_EVENT, handler);
    return () => window.removeEventListener(LVS_CONSENT_EVENT, handler);
  }, []);

  if (!visible) return null;

  const acceptAll = () => {
    writeConsent({ analytics: "granted", marketing: "granted" });
  };

  const rejectAll = () => {
    writeConsent({ analytics: "denied", marketing: "denied" });
  };

  const saveCustom = () => {
    writeConsent({
      analytics: analytics ? "granted" : "denied",
      marketing: marketing ? "granted" : "denied",
    });
  };

  return (
    <div
      className="lvs-consent"
      role="dialog"
      aria-modal="false"
      aria-label={isEn ? "Cookie preferences" : "Preferenze cookie"}
    >
      <div className="lvs-consent__panel">
        <div className="lvs-consent__body">
          <p className="lvs-consent__title">
            <strong>
              {isEn
                ? "Just one moment before you continue"
                : "Un momento prima di proseguire"}
            </strong>
          </p>
          <p className="lvs-consent__text">
            {isEn ? (
              <>
                We use technical cookies (required for the site to work)
                and, with your consent, analytics cookies to understand
                how visitors interact with our pages.{" "}
                <Link to={lp("/cookie-policy")}>Read the cookie policy</Link>{" "}
                or <Link to={lp("/privacy")}>the privacy policy</Link>.
              </>
            ) : (
              <>
                Usiamo cookie tecnici (necessari per il funzionamento del
                sito) e, con il tuo consenso, cookie analitici per capire
                come i visitatori interagiscono con le pagine.{" "}
                <Link to={lp("/cookie-policy")}>Leggi la cookie policy</Link>{" "}
                o <Link to={lp("/privacy")}>la privacy policy</Link>.
              </>
            )}
          </p>

          {showDetails && (
            <div className="lvs-consent__details">
              <label className="lvs-consent__row">
                <input type="checkbox" checked disabled readOnly />
                <span>
                  <strong>{isEn ? "Necessary" : "Necessari"}</strong> —{" "}
                  {isEn
                    ? "always on (required for the site to work)."
                    : "sempre attivi (funzionamento del sito)."}
                </span>
              </label>
              <label className="lvs-consent__row">
                <input
                  type="checkbox"
                  checked={analytics}
                  onChange={(e) => setAnalytics(e.target.checked)}
                />
                <span>
                  <strong>{isEn ? "Analytics" : "Analitici"}</strong> —{" "}
                  {isEn
                    ? "help us understand which pages work best (Google Analytics 4 via GTM)."
                    : "ci aiutano a capire quali pagine funzionano meglio (Google Analytics 4 via GTM)."}
                </span>
              </label>
              <label className="lvs-consent__row">
                <input
                  type="checkbox"
                  checked={marketing}
                  onChange={(e) => setMarketing(e.target.checked)}
                />
                <span>
                  <strong>Marketing</strong> —{" "}
                  {isEn
                    ? "to personalize any ads. Not currently active."
                    : "per personalizzare eventuali annunci. Attualmente non attivi."}
                </span>
              </label>
            </div>
          )}
        </div>

        <div className="lvs-consent__actions">
          {!showDetails ? (
            <>
              <button
                type="button"
                className="lvs-consent__btn lvs-consent__btn--ghost"
                onClick={() => setShowDetails(true)}
              >
                {isEn ? "Customize" : "Personalizza"}
              </button>
              <button
                type="button"
                className="lvs-consent__btn lvs-consent__btn--ghost"
                onClick={rejectAll}
              >
                {isEn ? "Reject" : "Rifiuta"}
              </button>
              <button
                type="button"
                className="lvs-consent__btn lvs-consent__btn--primary"
                onClick={acceptAll}
              >
                {isEn ? "Accept all" : "Accetta tutti"}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                className="lvs-consent__btn lvs-consent__btn--ghost"
                onClick={() => setShowDetails(false)}
              >
                {isEn ? "Back" : "Indietro"}
              </button>
              <button
                type="button"
                className="lvs-consent__btn lvs-consent__btn--primary"
                onClick={saveCustom}
              >
                {isEn ? "Save preferences" : "Salva preferenze"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
